import React from "react"
import { Helmet } from "react-helmet"
import { useLocation } from "@reach/router"
import { useStaticQuery, graphql } from "gatsby"

type SEOProps = {
  title?: string
  description?: string
  image?: string
  imageAlt?: string
  imageWidth?: number
  imageHeight?: number
  type?: string
  article?: string | boolean
}

const toAbsoluteUrl = (siteUrl: string, value?: string) => {
  if (!value) return undefined
  if (/^https?:\/\//i.test(value)) return value

  const path = value.startsWith("/") ? value : `/${value}`
  return `${siteUrl.replace(/\/$/, "")}${path}`
}

const getMetaTitle = (
  title: string | undefined,
  defaultTitle: string,
  titleTemplate: string
) => {
  const pageTitle = title || defaultTitle
  const isDefaultTitle = !title || title === defaultTitle

  return {
    pageTitle,
    titleTemplate: isDefaultTitle ? `%s` : titleTemplate,
    metaTitle: isDefaultTitle
      ? defaultTitle
      : titleTemplate.replace(`%s`, pageTitle),
  }
}

const SEO = ({
  title,
  description,
  image,
  imageAlt,
  imageWidth,
  imageHeight,
  type,
  article,
}: SEOProps) => {
  const { pathname } = useLocation()
  const { site } = useStaticQuery(query)

  const {
    defaultTitle,
    titleTemplate,
    defaultDescription,
    siteUrl,
    defaultImage,
    defaultImageAlt,
    defaultImageWidth,
    defaultImageHeight,
  } = site.siteMetadata
  const {
    pageTitle,
    titleTemplate: resolvedTitleTemplate,
    metaTitle,
  } = getMetaTitle(title, defaultTitle, titleTemplate)

  const seo = {
    title: pageTitle,
    titleTemplate: resolvedTitleTemplate,
    metaTitle,
    description: description || defaultDescription,
    image: toAbsoluteUrl(siteUrl, image || defaultImage),
    imageAlt: imageAlt || defaultImageAlt,
    imageWidth: imageWidth || defaultImageWidth,
    imageHeight: imageHeight || defaultImageHeight,
    type: type || (article ? `article` : `website`),
    url: `${siteUrl.replace(/\/$/, "")}${pathname}`,
  }

  return (
    <Helmet title={seo.title} titleTemplate={seo.titleTemplate}>
      <html lang="en" />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        rel="preconnect"
        href="https://fonts.gstatic.com"
        crossOrigin="anonymous"
      />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=Space+Grotesk:wght@400;500;600;700&display=swap"
      />
      <meta name="theme-color" content="#000000" />
      <link rel="canonical" href={seo.url} />
      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      {seo.url && <meta property="og:url" content={seo.url} />}
      <meta property="og:type" content={seo.type} />
      <meta property="og:site_name" content={defaultTitle} />
      <meta property="og:locale" content="en_US" />
      {seo.metaTitle && <meta property="og:title" content={seo.metaTitle} />}
      {seo.description && (
        <meta property="og:description" content={seo.description} />
      )}
      {seo.image && <meta property="og:image" content={seo.image} />}
      {seo.imageAlt && <meta property="og:image:alt" content={seo.imageAlt} />}
      {seo.imageWidth && (
        <meta property="og:image:width" content={seo.imageWidth} />
      )}
      {seo.imageHeight && (
        <meta property="og:image:height" content={seo.imageHeight} />
      )}
      <meta name="twitter:card" content="summary_large_image" />
      {seo.metaTitle && <meta name="twitter:title" content={seo.metaTitle} />}
      {seo.description && (
        <meta name="twitter:description" content={seo.description} />
      )}
      {seo.image && <meta name="twitter:image" content={seo.image} />}
      {seo.imageAlt && <meta name="twitter:image:alt" content={seo.imageAlt} />}
    </Helmet>
  )
}
export default SEO

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        defaultTitle: title
        titleTemplate
        defaultDescription: description
        siteUrl: url
        defaultImage: image
        defaultImageAlt: imageAlt
        defaultImageWidth: imageWidth
        defaultImageHeight: imageHeight
      }
    }
  }
`
